import React, { Dispatch, SetStateAction } from 'react';
import { StyleProp, TextStyle, View } from 'react-native';
import TimePicker from 'react-time-picker';
import { UnistylesRuntime } from 'react-native-unistyles';
import DatePicker from 'react-date-picker';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import 'react-date-picker/dist/DatePicker.css';
import 'react-calendar/dist/Calendar.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar } from '@fortawesome/free-solid-svg-icons';
import MyAppText from '../../MyAppText';
import '../../../styles/date-picker.css';
import { Controller, UseFormReturn } from 'react-hook-form';

type FilterDatePickerProps = {
    label: string;
    editable?: boolean;
    maxDate?: Date;
    zIndex?: number;
    onCalendarOpen?: () => void;
    onCalendarClose?: () => void;
    required?: boolean;
    disabled?: boolean;
    labelStyle?: StyleProp<TextStyle>;
    viewStyle?: StyleProp<TextStyle>;
} & ({
    form?: undefined;
    dateFieldName?: undefined;
    timeFieldName?: undefined;
    date: CalendarValue;
    onChangeDate: Dispatch<SetStateAction<CalendarValue>>;
    time: string;
    onChangeTime: (value: string) => void;
} | {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    form?: UseFormReturn<any, any, any>;
    dateFieldName: string;
    timeFieldName: string;
    date?: undefined;
    onChangeDate?: undefined;
    time?: undefined;
    onChangeTime?: undefined;
});

type ValuePiece = Date | null;
export type CalendarValue = ValuePiece;

export default function FilterDatePicker({ label, date, time, onChangeDate, onChangeTime, maxDate, zIndex, onCalendarOpen, onCalendarClose, required, disabled, labelStyle, viewStyle, form, dateFieldName, timeFieldName }: FilterDatePickerProps) {
    const { styles, theme } = useStyles(styleSheet);

    if (form && dateFieldName && timeFieldName) {
        return (
            <View style={[viewStyle ?? { rowGap: 5, flex: 1 }, zIndex ? { zIndex: zIndex } : {}]}>
                <View style={{ flexDirection: 'row', gap: 3 }}>
                    <MyAppText style={[labelStyle ?? styles.filterLabel]}>{label}</MyAppText>
                    {required ?
                        <MyAppText style={{ color: theme.colors.errors['300'] }}>(*)</MyAppText>
                        : null}
                </View>
                <View style={{ flexDirection: 'row', columnGap: 5, flex: 1 }}>
                    <View style={[styles.container(undefined), { flexGrow: 2 }]}>
                        <Controller
                            control={form.control}
                            name={dateFieldName}
                            render={({ field }) => (
                                <DatePicker
                                    calendarProps={{ locale: 'pt-BR' }}
                                    maxDate={maxDate}
                                    onChange={(val) => {
                                        field.onChange(val);
                                    }}
                                    autoFocus={false}
                                    shouldOpenCalendar={({ reason }) => reason !== 'focus'}
                                    value={field.value}
                                    clearIcon={null}
                                    disabled={disabled}
                                    locale='pt-BR'
                                    className={[
                                        'light', 'lightAlt'
                                    ].includes(UnistylesRuntime.themeName)
                                        ? ['inputColor', `inputColorLight`]
                                        : ['inputColor', `inputColorDark`]
                                    }
                                    calendarIcon={<FontAwesomeIcon icon={faCalendar} color={theme.colors.iconColor}></FontAwesomeIcon>}
                                    onCalendarClose={onCalendarClose}
                                    onCalendarOpen={onCalendarOpen}
                                />
                            )}
                        />

                    </View>
                    <Controller
                        control={form.control}
                        name={timeFieldName}
                        render={({ field }) => (
                            <TimePicker
                                disableClock={true}
                                format='HH:mm'
                                locale='pt-BR'
                                maxDetail='minute'
                                disabled={disabled}
                                className={[
                                    'light', 'lightAlt'
                                ].includes(UnistylesRuntime.themeName)
                                    ? ['inputColor', `inputColorLight`]
                                    : ['inputColor', `inputColorDark`]
                                }
                                value={field.value}
                                clearIcon={null}
                                onChange={(value) => {
                                    if (value) {
                                        field.onChange(value);
                                    }
                                }}
                            />
                        )}
                    />
                </View>
            </View>
        );
    } else if (date !== undefined && onChangeDate !== undefined && time !== undefined && onChangeTime !== undefined) {
        return (
            <View style={[viewStyle ?? { rowGap: 5, flex: 1 }, zIndex ? { zIndex: zIndex } : {}]}>
                <View style={{ flexDirection: 'row', gap: 3 }}>
                    <MyAppText style={[labelStyle ?? styles.filterLabel]}>{label}</MyAppText>
                    {required ?
                        <MyAppText style={{ color: theme.colors.errors['300'] }}>(*)</MyAppText>
                        : null}
                </View>
                <View style={{ flexDirection: 'row', columnGap: 5, flex: 1 }}>
                    <View style={[styles.container(undefined), { flexGrow: 2 }]}>
                        <DatePicker
                            calendarProps={{ locale: 'pt-BR' }}
                            maxDate={maxDate}
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            onChange={onChangeDate}
                            autoFocus={false}
                            shouldOpenCalendar={({ reason }) => reason !== 'focus'}
                            value={date}
                            clearIcon={null}
                            disabled={disabled}
                            locale='pt-BR'
                            className={[
                                'light', 'lightAlt'
                            ].includes(UnistylesRuntime.themeName)
                                ? ['inputColor', `inputColorLight`]
                                : ['inputColor', `inputColorDark`]
                            }
                            calendarIcon={<FontAwesomeIcon icon={faCalendar} color={theme.colors.iconColor}></FontAwesomeIcon>}
                            onCalendarClose={onCalendarClose}
                            onCalendarOpen={onCalendarOpen}
                        />
                    </View>
                    <TimePicker
                        disableClock={true}
                        format='HH:mm'
                        locale='pt-BR'
                        maxDetail='minute'
                        disabled={disabled}
                        className={[
                            'light', 'lightAlt'
                        ].includes(UnistylesRuntime.themeName)
                            ? ['inputColor', `inputColorLight`]
                            : ['inputColor', `inputColorDark`]
                        }
                        value={time}
                        clearIcon={null}
                        onChange={(value) => {
                            if (value) {
                                onChangeTime(value);
                            }
                        }}
                    />
                </View>
            </View>
        );
    }
}


const styleSheet = createStyleSheet((theme) => ({
    filterLabel: {
        color: theme.colors.labelColor,
        fontSize: 16,
        height: 25,
    },
    container: (zIndex?: number) => ({
        zIndex,
        '--daterange-invalid-border-color': theme.colors.errors['500'],
        '--daterange-text-color': theme.colors.textColor,
        '--daterange-border-color': theme.colors.field.border,
        '--daterange-button-color': theme.colors.iconColor,

        '--daterange-calendar-background-color': theme.colors.calendar.background,
        '--daterange-calendar-border-color': theme.colors.calendar.border,
        '--daterange-calendar-days-text-color': theme.colors.calendar.dayText,
        '--daterange-calendar-navigation-text-color': theme.colors.calendar.navigationText,
        '--daterange-calendar-navigation-text-disabled-color': theme.colors.calendar.navigationDisabledText,
        '--daterange-calendar-navigation-background-hover-color': theme.colors.calendar.navigationBackgroundHover,
        '--daterange-calendar-days-background-selected-active-color': theme.colors.calendar.dayBackgroundSelectedActive,
        '--daterange-calendar-days-background-selected-range-color': theme.colors.calendar.dayBackgroundSelectedRange,
        '--daterange-calendar-days-text-selected-range-color': theme.colors.calendar.dayTextSelectedRangeColor,
        '--daterange-calendar-days-text-disabled-color': theme.colors.calendar.dayTextDisabledColor,
    }),
}));
